// Phase 5 (web revival) — Stream D.
//
// Seven sliders for the seven scalar color ops. Each slider owns its own
// op slot keyed by the op name — set when |v| > 0.01, removed otherwise
// so a "neutral" edit doesn't bloat the wire payload with no-op rows.

import { useId } from 'react';

export type ColorOpName =
  | 'brightness'
  | 'contrast'
  | 'saturation'
  | 'exposure'
  | 'shadows'
  | 'highlights'
  | 'vignette';

export const COLOR_OP_NAMES: ColorOpName[] = [
  'brightness',
  'contrast',
  'saturation',
  'exposure',
  'shadows',
  'highlights',
  'vignette',
];

interface ColorSlidersProps {
  values: Record<ColorOpName, number>;
  onChange: (op: ColorOpName, v: number) => void;
}

const LABELS: Record<ColorOpName, string> = {
  brightness: 'Brightness',
  contrast: 'Contrast',
  saturation: 'Saturation',
  exposure: 'Exposure',
  shadows: 'Shadows',
  highlights: 'Highlights',
  vignette: 'Vignette',
};

export function ColorSliders({ values, onChange }: ColorSlidersProps) {
  return (
    <div>
      <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-neutral-400">
        Color
      </h3>
      <div className="space-y-3">
        {COLOR_OP_NAMES.map((op) => (
          <Slider
            key={op}
            name={op}
            label={LABELS[op]}
            value={values[op] ?? 0}
            onChange={(v) => onChange(op, v)}
          />
        ))}
      </div>
    </div>
  );
}

function Slider({
  name,
  label,
  value,
  onChange,
}: {
  name: ColorOpName;
  label: string;
  value: number;
  onChange: (v: number) => void;
}) {
  const id = useId();
  return (
    <div>
      <div className="mb-1 flex items-center justify-between">
        <label htmlFor={id} className="text-xs text-neutral-300">
          {label}
        </label>
        <span className="font-mono text-[11px] text-neutral-500 tabular-nums">
          {value > 0 ? '+' : ''}
          {value.toFixed(2)}
        </span>
      </div>
      <input
        id={id}
        name={name}
        type="range"
        min={-1}
        max={1}
        step={0.05}
        value={value}
        onChange={(e) => onChange(Number.parseFloat(e.target.value))}
        className="h-1.5 w-full cursor-pointer appearance-none rounded-full bg-neutral-800 accent-neutral-100"
      />
    </div>
  );
}
